import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, UserPlus } from "lucide-react";
import { createToken } from "../lib/api";

export default function ManualEntryModal({ shop, services, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (services?.length && selected.size === 0) {
      setSelected(new Set([services[0].id]));
    }
  }, [services]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalMin = services
    .filter((s) => selected.has(s.id))
    .reduce((sum, s) => sum + (s.duration_minutes || 0), 0);

  const canSave = name.trim().length >= 2 && selected.size > 0 && !saving;

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    if (!canSave) return;
    setSaving(true);
    try {
      const selectedServices = services.filter((s) => selected.has(s.id));
      const serviceName = selectedServices.map((s) => s.name).join(" + ");
      const tok = await createToken(shop.id, {
        customer_name: name.trim(),
        whatsapp: phone.trim() || "walk-in",
        avatar: "male",
        service_id: selectedServices[0]?.id || null,
        service_name: notes.trim() ? `${serviceName} (${notes.trim()})` : serviceName,
      });
      toast.success(`Walk-in token #${tok.token_number} added`);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-5"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.96 }}
        transition={{ duration: 0.35 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong rounded-2xl w-full max-w-md p-6 md:p-8"
        data-testid="manual-entry-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-gold flex items-center gap-2">
              <UserPlus className="w-3.5 h-3.5" /> Walk-In Entry
            </div>
            <div className="font-heading text-2xl mt-1">Add Customer Manually</div>
          </div>
          <button onClick={onClose} className="p-2 text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-6 space-y-5">
          <Field label="Customer Name">
            <input
              className="input-minimal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              autoFocus
              data-testid="manual-name"
            />
          </Field>
          <Field label="Phone (optional)">
            <input
              className="input-minimal font-numbers"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              inputMode="tel"
              data-testid="manual-phone"
            />
          </Field>

          <div>
            <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
              Services (tap to select multiple)
            </label>
            <div className="mt-2 max-h-44 overflow-y-auto pr-1 space-y-1.5">
              {services.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggle(s.id)}
                  data-testid={`manual-svc-${s.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2 border text-sm transition-all flex items-center justify-between ${
                    selected.has(s.id)
                      ? "border-gold bg-gold/5 text-gold"
                      : "border-white/10 text-white/70 hover:border-white/30"
                  }`}
                >
                  <span>{s.name}</span>
                  <span className="font-numbers text-[10px] tracking-wider">
                    {s.duration_minutes}m
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-2 text-[10px] font-numbers tracking-[0.25em] uppercase text-white/40">
              {selected.size} selected · est. total {totalMin}m
            </div>
          </div>

          <Field label="Notes (optional)">
            <input
              className="input-minimal"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="VIP · paid in cash · refer-by Anu"
              data-testid="manual-notes"
            />
          </Field>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button
            onClick={save}
            disabled={!canSave}
            data-testid="manual-save"
            className="btn-gold text-sm py-2.5 px-5 inline-flex items-center gap-2"
          >
            {saving ? "Adding…" : "Add to Queue"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
        {label}
      </label>
      {children}
    </div>
  );
}
